import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import KnowledgeBase from "@/pages/KnowledgeBase";
import Workflows from "@/pages/Workflows";
import Assistant from "@/pages/Assistant";
import AICenter from "@/pages/AICenter";
import Analytics from "@/pages/Analytics";
import Leads from "@/pages/Leads";
import LeadRecovery from "@/pages/LeadRecovery";
import Customers from "@/pages/Customers";
import Products from "@/pages/Products";
import Personas from "@/pages/Personas";
import MarketingHub from "@/pages/MarketingHub";
import ContentPerformance from "@/pages/ContentPerformance";
import SocialMedia from "@/pages/SocialMedia";
import PaidAdvertising from "@/pages/PaidAdvertising";
import WebsiteAnalytics from "@/pages/WebsiteAnalytics";
import Websites from "@/pages/Websites";
import Recommendations from "@/pages/Recommendations";
import Campaigns from "@/pages/Campaigns";
import Partners from "@/pages/Partners";
import Forms from "@/pages/Forms";
import PublicForm from "@/pages/PublicForm";
import LandingPages from "@/pages/LandingPages";
import PublicLandingPage from "@/pages/PublicLandingPage";
import NurtureSequences from "@/pages/NurtureSequences";
import NurtureSequenceDetail from "@/pages/NurtureSequenceDetail";
import PipelinesManage from "@/pages/PipelinesManage";
import Platform from "@/pages/Platform";
import InviteAccept from "@/pages/InviteAccept";

function AppRouter() {
  const location = useLocation();
  // Detect Emergent Auth callback BEFORE normal routing to avoid race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/invite/:token" element={<InviteAccept />} />
      {/* PUBLIC form route — no auth wrapper. Must sit outside ProtectedRoute. */}
      <Route path="/f/:slug" element={<PublicForm />} />
      {/* PUBLIC landing page route — no auth wrapper. */}
      <Route path="/p/:slug" element={<PublicLandingPage />} />
      <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/ai-center" element={<AICenter />} />
        <Route path="/ai-center/recommendations" element={<Recommendations />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/assistant" element={<Assistant />} />
        <Route path="/knowledge-base" element={<KnowledgeBase />} />
        <Route path="/workflows" element={<Workflows />} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/leads/recovery" element={<LeadRecovery />} />
        <Route path="/leads/pipelines" element={<PipelinesManage />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/products" element={<Products />} />
        <Route path="/personas" element={<Personas />} />
        <Route path="/marketing" element={<MarketingHub />} />
        <Route path="/marketing/content" element={<ContentPerformance />} />
        <Route path="/marketing/social" element={<SocialMedia />} />
        <Route path="/marketing/paid" element={<PaidAdvertising />} />
        <Route path="/marketing/website-analytics" element={<WebsiteAnalytics />} />
        <Route path="/websites" element={<Websites />} />
        <Route path="/campaigns" element={<Campaigns />} />
        <Route path="/partners" element={<Partners />} />
        <Route path="/forms" element={<Forms />} />
        <Route path="/landing-pages" element={<LandingPages />} />
        <Route path="/nurture-sequences" element={<NurtureSequences />} />
        <Route path="/nurture-sequences/:sequenceId" element={<NurtureSequenceDetail />} />
        <Route path="/platform" element={<Platform />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster
            theme="dark"
            position="bottom-right"
            toastOptions={{
              style: {
                background: "#121418",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#F8FAFC",
                fontFamily: "IBM Plex Sans, sans-serif",
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
